"use client";

/**
 * Configure modal — the enrollment funnel's entry surface (spec §5).
 *
 * A green clone of the talent-search export wizard: a two-panel modal with a
 * branded left sidebar and a numbered 3-step flow on the right —
 *
 *   1. Sequence    — pick an active/paused sequence for the cohort.
 *   2. Enrichment  — choose the waterfall strategy for resolving contacts.
 *   3. Review      — credit estimate + hard gate before previewing.
 *
 * Both entry points (job pipeline + talent search) hydrate `useConfigureStore`
 * with the cohort + jobId, then mount this modal. Step navigation lives in local
 * React state; only the persistent cohort payload lives in the store.
 *
 * On Preview the modal swaps its body for a progress view: while `create` is
 * pending the backend reveals contacts (charging credits) — shown as an
 * indeterminate ring. Once a `preview_session_id` comes back we poll the preview
 * session and show determinate render progress. When rendering completes we
 * refresh credit/contact caches, open the results drawer via `usePreviewStore`,
 * close the modal, and reset the configure store.
 *
 * A 402 (`ENROLLMENT_INSUFFICIENT_CREDITS`) from `create` drops back to the
 * review step with the insufficient banner; any other error toasts and returns.
 */

import * as React from "react";

import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";

import { Icon } from "@/components/ui/icon";
import { Modal, ModalContent, ModalTitle } from "@/components/ui/modal";
import { showToast } from "@/components/ui/sonner";
import { StepPanel, WizardStepIndicator } from "@/components/wizard";
import { ApiError, getApiErrorMessage } from "@/lib/api/error";

import { useEnrollmentEstimate } from "../hooks/use-enrollment-estimate";
import { useEnrollmentSequences } from "../hooks/use-enrollment-sequences";
import { useEnrollmentPreview } from "../hooks/use-enrollment-preview";
import { invalidateAfterReveal } from "../lib/invalidate-after-reveal";
import { ENROLLMENT_STEP_ACTIVE } from "../lib/theme";
import type { PreviewPhaseFields, WaterfallStrategy } from "../lib/types";
import { useConfigureStore } from "../store/configure-store";
import { usePreviewStore } from "../store/preview-store";
import {
  ConfigureWizardProvider,
  type ConfigureStep,
} from "./configure/context";
import { ConfigureProgress } from "./configure/progress";
import {
  StepAddToPosition,
  type StepAddToPositionResult,
} from "./configure/step-add-position";
import { StepEnrichment } from "./configure/step-enrichment";
import { StepReview } from "./configure/step-review";
import { StepSequence } from "./configure/step-sequence";

const MAX_COHORT = 50;

// Wider gutter than the export wizard's default; matches the step row above.
const STEP_PANEL_INSET = "px-6 sm:px-8";

const STEP_ORDER: ConfigureStep[] = [1, 2, 3];

const STEP_LABELS: Record<ConfigureStep, string> = {
  1: "Sequence",
  2: "Enrichment",
  3: "Review",
};

const SIDEBAR_FEATURES = [
  "Pick an active sequence",
  "Choose contact enrichment",
  "Review the credit cost",
  "Preview before enrolling",
];

export interface ConfigureModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

export default function ConfigureModal({
  open,
  onOpenChange,
}: ConfigureModalProps) {
  // The Modal (base-ui Dialog root) stays mounted across open/close so the
  // library runs its own close lifecycle (scroll-lock / focus / overlay
  // teardown) when `open` flips to false — remounting the root or the popup
  // mid-close strands the overlay and blocks dismissal. The shell (and its
  // data hooks) mounts only while open, which also gives a fresh wizard state
  // on every open without bleeding the previous session's selection.
  // Keep BOTH the Modal (base-ui Dialog root) and the shell mounted across
  // open/close so base-ui plays its graceful close animation and runs its
  // scroll-lock / focus / overlay teardown. Fresh per-open wizard state comes
  // from a reset-on-open effect in the shell (mirrors the export wizard), and
  // the shell's data hooks are gated on `open` so nothing fetches while closed.
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ConfigureModalShell open={open} onOpenChange={onOpenChange} />
    </Modal>
  );
}

/** Pull a backend error code out of either an `ApiError` or a raw envelope. */
function extractErrorCode(err: unknown): string | null {
  if (err instanceof ApiError) return err.code;
  if (err && typeof err === "object") {
    const detail = (err as { detail?: { code?: unknown } }).detail;
    if (detail && typeof detail.code === "string") return detail.code;
    const code = (err as { code?: unknown }).code;
    if (typeof code === "string") return code;
  }
  return null;
}

function ConfigureModalShell({ open, onOpenChange }: ConfigureModalProps) {
  const queryClient = useQueryClient();

  const jobId = useConfigureStore((s) => s.jobId);
  const jobCandidateIds = useConfigureStore((s) => s.jobCandidateIds);
  const profileIds = useConfigureStore((s) => s.profileIds);
  const storedSequenceId = useConfigureStore((s) => s.sequenceId);
  const storedWaterfall = useConfigureStore((s) => s.waterfallStrategy);
  const setStore = useConfigureStore((s) => s.set);
  const resetStore = useConfigureStore((s) => s.reset);
  const openDrawer = usePreviewStore((s) => s.openDrawer);

  const cohortSize = jobCandidateIds.length;
  const cohortTooLarge = cohortSize > MAX_COHORT;
  const cohortEmpty = cohortSize === 0;

  // Talent-search entry arrives with `profileIds` but no cohort yet — open on the
  // "Add to a position" pre-step. Every other entry point already has a cohort.
  const needsAddToPosition = profileIds.length > 0 && jobCandidateIds.length === 0;

  const [phase, setPhase] = React.useState<"add" | "form" | "progress">("form");
  const [step, setStep] = React.useState<ConfigureStep>(1);
  const [sequenceId, setSequenceId] = React.useState<string | null>(
    storedSequenceId,
  );
  const [waterfall, setWaterfall] =
    React.useState<WaterfallStrategy>(storedWaterfall);
  const [previewSessionId, setPreviewSessionId] = React.useState<string | null>(
    null,
  );
  const [insufficientFrom402, setInsufficientFrom402] = React.useState(false);
  // Guards the post-render handoff so it fires exactly once per preview session.
  const handedOffRef = React.useRef(false);

  // Reset to a clean wizard whenever the modal (re)opens. The shell stays
  // mounted across open/close (so base-ui can animate the close gracefully),
  // so we reset on the open edge rather than relying on a remount.
  const wasOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      // Syncing the external `open` prop into a fresh internal wizard state on
      // the open edge (same reset-on-open pattern as the export wizard). The
      // talent-search entry starts on the add-to-position pre-step; every other
      // entry point already has a cohort and starts on the sequence step.
      setPhase(needsAddToPosition ? "add" : "form");
      setStep(1);
      setSequenceId(storedSequenceId);
      setWaterfall(storedWaterfall);
      setPreviewSessionId(null);
      setInsufficientFrom402(false);
      handedOffRef.current = false;
    }
    wasOpenRef.current = open;
  }, [open, storedSequenceId, storedWaterfall, needsAddToPosition]);

  // ── Step 1 data ── (gated on `open` so a closed-but-mounted shell is idle)
  const {
    sequences,
    isLoading: sequencesLoading,
    hasNextPage: hasMoreSequences,
    isFetchingNextPage: loadingMoreSequences,
    fetchNextPage: loadMoreSequences,
  } = useEnrollmentSequences(open ? jobId : null, open);
  // Any non-archived sequence with at least one step is shown — there's no
  // separate "activate" step. Sequences that still have steps missing
  // required content render disabled (with a "needs configuration" tooltip);
  // archived + step-less sequences are hidden entirely.
  const eligibleSequences = React.useMemo(
    () => sequences.filter((s) => !s.archived_at && (s.step_count ?? 0) > 0),
    [sequences],
  );
  const hasAnySequences = sequences.length > 0;

  // ── Step 3 estimate ── (only while reviewing, never during progress)
  const estimateQuery = useEnrollmentEstimate({
    jobId: jobId ?? "",
    sequenceId,
    jobCandidateIds,
    enabled: open && phase === "form" && step === 3 && !cohortTooLarge,
  });

  // ── Preview lifecycle ── create (handoff) + get (render polling)
  const { create, get } = useEnrollmentPreview({
    jobId: jobId ?? undefined,
    sequenceId: sequenceId ?? undefined,
    previewSessionId: previewSessionId ?? undefined,
    enabled: open && phase === "progress" && !!previewSessionId,
  });

  const selectSequence = React.useCallback(
    (id: string) => {
      setSequenceId(id);
      setStore({ sequenceId: id });
    },
    [setStore],
  );

  const selectWaterfall = React.useCallback(
    (value: WaterfallStrategy) => {
      setWaterfall(value);
      setStore({ waterfallStrategy: value });
    },
    [setStore],
  );

  // Add-to-position pre-step finished: persist the new job + cohort, clear the
  // pending profile ids, and slide into the sequence step. The sequence /
  // estimate hooks read `jobId` from the store, so they wake up on this update.
  const handleAddedToPosition = React.useCallback(
    ({ jobId: nextJobId, jobCandidateIds: nextCohort }: StepAddToPositionResult) => {
      setStore({
        jobId: nextJobId,
        jobCandidateIds: nextCohort,
        profileIds: [],
      });
      setStep(1);
      setPhase("form");
    },
    [setStore],
  );

  const startPreview = React.useCallback(async () => {
    if (!jobId || !sequenceId || jobCandidateIds.length === 0) return;
    setInsufficientFrom402(false);
    setPreviewSessionId(null);
    setPhase("progress");
    try {
      const result = await create.mutateAsync({
        jobId,
        sequenceId,
        data: {
          job_candidate_ids: jobCandidateIds,
          waterfall_strategy: waterfall,
          sequence_id: sequenceId,
        },
      });
      setPreviewSessionId(result.preview_session_id);
    } catch (err) {
      setPhase("form");
      setStep(3);
      const code = extractErrorCode(err);
      if (code === "ENROLLMENT_INSUFFICIENT_CREDITS") {
        setInsufficientFrom402(true);
      } else if (code === "SENDER_MAILBOX_NOT_CONNECTED") {
        // Belt-and-suspenders: the picker already disables this sequence, but
        // if a stale list let it through, the create gate blocks it — explain
        // why instead of a generic failure.
        showToast.error(
          "The sender's mailbox needs to be reconnected before you can enroll. Reconnect it in Settings → Connected Accounts.",
        );
      } else if (code === "STEPS_NOT_CONFIGURED") {
        showToast.error(
          "This sequence has steps that need configuration before you can enroll.",
        );
      } else if (code === "SEQUENCE_HAS_NO_STEPS") {
        // The picker hides step-less sequences, but a stale list / direct
        // seed can still reach the create gate — explain the real blocker.
        showToast.error(
          "This sequence has no steps. Add at least one step before you can enroll candidates.",
        );
      } else {
        showToast.error(
          "Couldn't start the enrollment preview. Please try again.",
          { description: getApiErrorMessage(err) },
        );
      }
    }
  }, [jobId, sequenceId, jobCandidateIds, waterfall, create]);

  // Render polling: refetch the preview session until it reports complete.
  // The backend prepares the session through a Temporal workflow
  // (revealing → preparing → rendering → ready), so the poll covers BOTH
  // the contact reveal and the content render with live counters.
  const previewData = get.data as
    | (typeof get.data & PreviewPhaseFields)
    | undefined;
  const renderComplete = previewData?.render_complete === true;
  const previewPhase = previewData?.phase;
  const pipelineFailed = previewPhase === "failed";

  React.useEffect(() => {
    if (
      !open ||
      phase !== "progress" ||
      !previewSessionId ||
      renderComplete ||
      pipelineFailed
    )
      return;
    const id = window.setInterval(() => {
      // The error transition lives in the async callback (not the effect
      // body) so a failed render (410 TTL expiry / 500 mid-render) drops the
      // user back to review instead of stranding them on the spinner.
      void get.refetch().then((res) => {
        if (res.isError) {
          setPhase("form");
          setStep(3);
          setPreviewSessionId(null);
          showToast.error(
            "Couldn't generate the preview. Please try again in a moment.",
            { description: getApiErrorMessage(res.error) },
          );
        }
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [open, phase, previewSessionId, renderComplete, pipelineFailed, get]);

  // Pipeline failure (phase="failed" stamped by the prepare workflow):
  // drop back to review with the most specific error we know.
  React.useEffect(() => {
    if (!open || phase !== "progress" || !pipelineFailed) return;
    const code = previewData?.phase_error_code;
    setPhase("form");
    setStep(3);
    setPreviewSessionId(null);
    if (code === "ENROLLMENT_INSUFFICIENT_CREDITS") {
      setInsufficientFrom402(true);
    } else if (code === "preview_reveal_failed") {
      showToast.error(
        "Couldn't reveal contacts for this cohort. Please try again in a moment.",
      );
    } else {
      showToast.error(
        "Couldn't generate the preview. Please try again in a moment.",
      );
    }
  }, [open, phase, pipelineFailed, previewData?.phase_error_code]);

  // Handoff once rendering completes: refresh caches, open the drawer, close.
  React.useEffect(() => {
    if (handedOffRef.current) return;
    if (!open || phase !== "progress" || !previewSessionId || !renderComplete)
      return;
    if (!jobId || !sequenceId) return;
    handedOffRef.current = true;
    invalidateAfterReveal(queryClient);
    openDrawer({ jobId, sequenceId, previewSessionId });
    onOpenChange(false);
    resetStore();
  }, [
    open,
    phase,
    previewSessionId,
    renderComplete,
    jobId,
    sequenceId,
    queryClient,
    openDrawer,
    onOpenChange,
    resetStore,
  ]);

  // Determinate render progress from rendered cells / total cells.
  const { doneCells, totalCells } = React.useMemo(() => {
    const candidates = previewData?.candidates ?? [];
    let done = 0;
    let total = 0;
    for (const candidate of candidates) {
      const steps = candidate.steps ?? [];
      total += steps.length;
      for (const cell of steps) {
        if (cell.status === "rendered" || cell.status === "failed") done += 1;
      }
    }
    return { doneCells: done, totalCells: total };
  }, [previewData]);

  // Phase-aware medallion state. "revealing" covers everything before the
  // render stage (create in flight, first poll not yet landed, contact
  // reveal fan-out, cohort load); "generating" is the per-cell content
  // render. The `!previewData` clause matters: right after create resolves
  // there is a session id but no poll data yet — without it the medallion
  // briefly flipped to "generating", then back to "revealing" when the
  // first poll landed. Legacy sessions (data present, no phase field)
  // still fall through to "generating".
  const revealing =
    !previewSessionId ||
    !previewData ||
    previewPhase === "revealing" ||
    previewPhase === "preparing";
  const revealDone = previewData?.reveal_done ?? 0;
  const revealTotal = previewData?.reveal_total ?? 0;
  const revealCached = previewData?.reveal_cached ?? 0;
  const progressValue = renderComplete
    ? 1
    : revealing
      ? revealTotal > 0
        ? revealDone / revealTotal
        : 0
      : totalCells > 0
        ? doneCells / totalCells
        : 0;
  const progressDone = revealing ? revealDone : doneCells;
  const progressTotal = revealing ? revealTotal : totalCells;

  const estimate = estimateQuery.data;
  const previewDisabled =
    cohortEmpty ||
    cohortTooLarge ||
    !sequenceId ||
    estimateQuery.isLoading ||
    estimateQuery.isError ||
    !estimate ||
    !estimate.sufficient ||
    insufficientFrom402 ||
    create.isPending;

  const onCancel = React.useCallback(
    () => onOpenChange(false),
    [onOpenChange],
  );

  const wizardValue = React.useMemo(
    () => ({
      cohortSize,
      step,
      setStep,
      jobId,
      eligibleSequences,
      hasAnySequences,
      sequencesLoading,
      hasMoreSequences,
      loadingMoreSequences,
      loadMoreSequences,
      sequenceId,
      selectSequence,
      waterfall,
      selectWaterfall,
      estimate,
      estimateLoading: estimateQuery.isLoading,
      estimateError: estimateQuery.isError,
      cohortTooLarge,
      cohortEmpty,
      previewDisabled,
      insufficientFrom402,
      startPreview,
      onCancel,
    }),
    [
      cohortSize,
      step,
      jobId,
      eligibleSequences,
      hasAnySequences,
      sequencesLoading,
      hasMoreSequences,
      loadingMoreSequences,
      loadMoreSequences,
      sequenceId,
      selectSequence,
      waterfall,
      selectWaterfall,
      estimate,
      estimateQuery.isLoading,
      estimateQuery.isError,
      cohortTooLarge,
      cohortEmpty,
      previewDisabled,
      insufficientFrom402,
      startPreview,
      onCancel,
    ],
  );

  // During the add pre-step the cohort doesn't exist yet, so the header counts
  // the picked profiles; afterwards it counts the candidates actually enrolling.
  const displayCount = phase === "add" ? profileIds.length : cohortSize;
  const profileWord = displayCount === 1 ? "profile" : "profiles";

  return (
    <ModalContent size="lg" className="max-h-[92dvh] p-0 sm:max-w-180">
      <div className="relative flex min-h-0 flex-col overflow-hidden sm:h-140 sm:flex-row">
        {/* ── Left branded sidebar ── */}
        <div className="hidden w-[280px] shrink-0 flex-col justify-between border-r border-border bg-[#87FFA5] px-7 py-8 sm:flex">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Icon name="IconPaperPlane" size={22} className="text-black" />
              <span className="text-base font-semibold text-black">Enroll</span>
            </div>
            <div className="mt-4 flex flex-col gap-2">
              <ModalTitle className="text-2xl font-semibold leading-tight text-black">
                Enroll {displayCount} {profileWord} into Sequence
              </ModalTitle>
              <p className="text-[13px] leading-relaxed text-black/60">
                {phase === "add"
                  ? "Add these profiles to a position first, then pick a sequence and review the credit cost before previewing."
                  : "Choose a sequence and how to find contact info, then review the credit cost before previewing."}
              </p>
            </div>
          </div>
          <div className="mt-8 flex flex-col gap-3">
            {SIDEBAR_FEATURES.map((f) => (
              <div key={f} className="flex items-center gap-2.5">
                <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-black/10">
                  <Icon name="IconCheckmark1" size={12} className="text-black" />
                </div>
                <span className="text-[13px] text-black/80">{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right content panel ── */}
        <div className="relative flex min-h-0 flex-1 flex-col bg-surface-elevated">
          {phase !== "progress" && (
            <button
              type="button"
              onClick={onCancel}
              className="absolute right-3.5 top-3.5 z-10 flex size-6 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-contrast/6"
            >
              <Icon
                name="IconCrossMedium"
                size={14}
                className="text-contrast/40 hover:text-contrast/70"
              />
            </button>
          )}

          {phase === "progress" ? (
            <ConfigureProgress
              mode={revealing ? "revealing" : "generating"}
              value={progressValue}
              done={progressDone}
              total={progressTotal}
              caption={
                revealing ? "Revealing contacts…" : "Generating previews…"
              }
              detail={
                revealing
                  ? revealCached > 0
                    ? `${revealCached} already on file, no credits used`
                    : undefined
                  : totalCells > 0
                    ? `${doneCells} of ${totalCells} previews ready`
                    : undefined
              }
            />
          ) : (
            // Slide between the add-to-position pre-step and the enrollment
            // wizard so the talent-search flow reads as one continuous motion.
            <AnimatePresence mode="wait">
              {phase === "add" ? (
                <motion.div
                  key="add"
                  className="flex min-h-0 flex-1 flex-col"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.15 }}
                >
                  <StepAddToPosition
                    profileIds={profileIds}
                    onAdded={handleAddedToPosition}
                    onCancel={onCancel}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  className="flex min-h-0 flex-1 flex-col"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.15 }}
                >
                  {/* Connectors are a touch tighter than export's so the longer
                      labels still clear the close button on the right. */}
                  <WizardStepIndicator
                    labels={STEP_ORDER.map((s) => STEP_LABELS[s])}
                    current={step}
                    activeClassName={ENROLLMENT_STEP_ACTIVE}
                    className="pl-6 sm:pl-8 sm:pr-8"
                    connectorClassName="sm:w-6"
                  />

                  <ConfigureWizardProvider value={wizardValue}>
                    <AnimatePresence mode="wait">
                      {step === 1 && (
                        <StepPanel key="s1" className={STEP_PANEL_INSET}>
                          <StepSequence />
                        </StepPanel>
                      )}
                      {step === 2 && (
                        <StepPanel key="s2" className={STEP_PANEL_INSET}>
                          <StepEnrichment />
                        </StepPanel>
                      )}
                      {step === 3 && (
                        <StepPanel key="s3" className={STEP_PANEL_INSET}>
                          <StepReview />
                        </StepPanel>
                      )}
                    </AnimatePresence>
                  </ConfigureWizardProvider>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
      </div>
    </ModalContent>
  );
}
